import { types } from 'actions/searchNLP';
import { withSession } from './sessions';

/**
 * Feedback telemetry handlers. Each appends to a session record's feedback log — the event
 * stream and aggregates that travel verbatim to POST /api/ai/search_feedback on submit.
 * Event `action` strings are a backend contract; do not rename them.
 */

/** Merge changes into a session's feedback record; `build` receives the current feedback. */
function withFeedback(state, sessionId, build) {
  return withSession(state, sessionId, existing => ({
    feedback: { ...existing.feedback, ...build(existing.feedback) },
  }));
}

export const feedbackEventHandlers = {
  [types.USER_SUBMITTED_PROMPT]: (state, action) => {
    const { sessionId, queryString } = action.payload;
    if (!sessionId) {
      return state;
    }
    return {
      ...state,
      sessions: withFeedback(state, sessionId, feedback => ({
        queries: [...feedback.queries, queryString],
        events: [
          ...feedback.events,
          { action: 'userSubmittedPrompt', query: queryString },
        ],
      })),
    };
  },
  [types.GENERATE_FILTERS_SUCCESS_RECEIVED]: (state, action) => {
    const { sessionId, response } = action.payload;
    if (!sessionId || !response) {
      return state;
    }
    return {
      ...state,
      sessions: withFeedback(state, sessionId, feedback => {
        const processingTimeMs =
          feedback.processingTimeMs + (response.processingTimeMs || 0);
        // The orchestrator reports rejections through the success channel with an errorCode.
        if (response.errorCode) {
          return {
            processingTimeMs,
            events: [
              ...feedback.events,
              {
                action: 'generateFiltersRejectionReceived',
                processingTimeMs: response.processingTimeMs,
                inputTokens: response.inputTokens,
                outputTokens: response.outputTokens,
                errorCode: response.errorCode,
                explanation: response.explanation,
                notFoundEntities: response.notFoundEntities,
              },
            ],
          };
        }
        return {
          processingTimeMs,
          filters: response.filters,
          events: [
            ...feedback.events,
            {
              action: 'generateFiltersSuccessReceived',
              filters: response.filters,
              termSynonyms: response.termSynonyms,
              termPlurals: response.termPlurals,
              processingTimeMs: response.processingTimeMs,
              inputTokens: response.inputTokens,
              outputTokens: response.outputTokens,
            },
          ],
        };
      }),
    };
  },
  [types.GENERATE_FILTERS_FAILURE_RECEIVED]: (state, action) => {
    const { sessionId } = action.payload;
    if (!sessionId) {
      return state;
    }
    return {
      ...state,
      sessions: withFeedback(state, sessionId, feedback => ({
        events: [
          ...feedback.events,
          { action: 'generateFiltersFailureReceived' },
        ],
      })),
    };
  },
  [types.SEARCH_RESULTS_RECEIVED]: (state, action) => {
    const { sessionId, response, view } = action.payload;
    if (!sessionId || !response) {
      return state;
    }
    return {
      ...state,
      sessions: withFeedback(state, sessionId, feedback => ({
        searchResultsCount: response.totalResults,
        searchType: view,
        events: [
          ...feedback.events,
          {
            action: 'searchResultsReceived',
            view,
            totalResults: response.totalResults,
            totalFunding: response.totalFunding,
            totalPosters: response.totalPosters,
            totalPublications: response.totalPublications,
            totalClinicalTrials: response.totalClinicalTrials,
            termCount: response.termCount,
          },
        ],
      })),
    };
  },
  [types.FOLLOW_UP_QUERY_SUCCESS_RECEIVED]: (state, action) => {
    const { sessionId, response } = action.payload;
    if (!sessionId) {
      return state;
    }
    return {
      ...state,
      sessions: withFeedback(state, sessionId, feedback => ({
        processingTimeMs:
          typeof response?.processingTimeMs === 'number'
            ? feedback.processingTimeMs + response.processingTimeMs
            : feedback.processingTimeMs,
        events: [
          ...feedback.events,
          { action: 'followUpQuerySuccessReceived', queries: response },
        ],
      })),
    };
  },
  [types.FOLLOW_UP_QUERY_FAILURE_RECEIVED]: (state, action) => {
    const { sessionId, fingerprint } = action.payload;
    if (!sessionId) {
      return state;
    }
    return {
      ...state,
      sessions: withSession(state, sessionId, existing => ({
        // Drives the panel's error state — a failed round, keyed to the fingerprint it was issued
        // under so a moved-on search doesn't read it as current. queries stays empty and count is
        // untouched so a failure doesn't consume one of MAX_FOLLOWUP_ROUNDS. A later success fully
        // replaces this object (no error key), clearing the flag.
        followUps: {
          sessionId,
          fingerprint,
          queries: [],
          count: existing.followUps.count,
          error: true,
        },
        feedback: {
          ...existing.feedback,
          events: [
            ...existing.feedback.events,
            { action: 'followUpQueryFailureReceived' },
          ],
        },
      })),
    };
  },
};
